import { useMemo } from "react";
import { now, startOf, toDate } from "../../../shared/lib/temporal";

export interface UseDateNavigationProps {
    selectedDate: Date | null;
    dates: Date[];
    timezone: string;
    onDateChange: (date: Date) => void;
}

/**
 * Custom hook for managing date navigation within a set of valid dates.
 *
 * Provides handlers for navigating to the previous day, next day, and today,
 * as well as flags for disabling navigation buttons based on the current selection.
 *
 * @param props - The properties for date navigation.
 * @param props.selectedDate - The currently selected date, or null if none is selected.
 * @param props.dates - Array of valid dates to navigate through.
 * @param props.timezone - The timezone string used for calculating "today".
 * @param props.onDateChange - Callback invoked when the selected date changes.
 *
 * @returns An object containing navigation handlers and disabled state flags:
 * - `handlePrevDay`: Navigates to the previous valid date.
 * - `handleNextDay`: Navigates to the next valid date.
 * - `handleToday`: Navigates to today's date if it exists in the valid dates.
 * - `isPrevDisabled`: Whether the previous day navigation is disabled.
 * - `isNextDisabled`: Whether the next day navigation is disabled.
 * - `isTodayDisabled`: Whether the today navigation is disabled.
 */
export const useDateNavigation = ({
    selectedDate,
    dates,
    timezone,
    onDateChange,
}: UseDateNavigationProps) => {
    // Build array of all valid dates (contiguous within deployments)
    const validDates = useMemo(() => {
        dates.sort((a, b) => a.getTime() - b.getTime());
        return dates;
    }, [dates]);

    const currentIndex = selectedDate
        ? validDates.findIndex((d) => d.getTime() === selectedDate.getTime())
        : -1;

    const handlePrevDay = () => {
        if (currentIndex > 0) {
            onDateChange(validDates[currentIndex - 1]);
        }
    };

    const handleNextDay = () => {
        if (currentIndex >= 0 && currentIndex < validDates.length - 1) {
            onDateChange(validDates[currentIndex + 1]);
        }
    };

    const handleToday = () => {
        const today = toDate(startOf(now(timezone), "day"));
        const todayIndex = validDates.findIndex(
            (d) => d.getTime() === today.getTime(),
        );
        if (todayIndex !== -1) {
            onDateChange(validDates[todayIndex]);
        }
    };

    const todayDate = toDate(startOf(now(timezone), "day"));

    return {
        handlePrevDay,
        handleNextDay,
        handleToday,
        isPrevDisabled: currentIndex <= 0,
        isNextDisabled:
            currentIndex === -1 || currentIndex >= validDates.length - 1,
        isTodayDisabled:
            validDates.findIndex((d) => d.getTime() === todayDate.getTime()) ===
            -1,
    };
};
